import {useCallback} from 'react';
import {useAppSelector} from 'store/customer';
import {TemplateVariable} from 'components/customer/Preview3D/usePreview3DData';
import {ConditionalValues, Drawer} from 'components/customer/Preview3D/types';
import {getBaseInnerDrawerFaceHeight} from 'components/customer/Preview3DCommon/store/viewerSlice';
import useMaterialTexture from 'components/customer/Preview3D/lib/useMaterialTexture';
import {
    generateDrawers as getDrawers,
    getDrawerFormValues,
    generateInnerDrawer as getInnerDrawers,
} from 'components/customer/Preview3DCommon/values/drawer';
import {use3DContext} from 'components/customer/Preview3D/components/Preview3DComponent';

export type DrawerValues = {
    faceHeight: number;
    depth: number;
    position: number;
    bottomPosition: number;
    bottomYPosition: number;
    drawerBottomYPosition: number;
    bottomDepth: number;
    depthOffset: number;
    index: number;
    drawerBackHeight: number;
    isInsetDrawer: boolean;
};

const useDrawerDynamicValues = () => {
    const {values, productDataStore} = use3DContext();

    const variables: TemplateVariable = JSON.parse(
        productDataStore.current?.template_3d[0].attributes?.variables
    ) as TemplateVariable;

    const innerDrawerFaceHeight = useAppSelector(getBaseInnerDrawerFaceHeight);
    const {exteriorThickness, carcaseThickness} = useMaterialTexture();

    const formValues = getDrawerFormValues(values);

    const generateDrawerPosition = useCallback(() => {
        return generateDrawers(
            formValues.drawers,
            false,
            variables?.isWallOvenProduct
                ? Number(values.cabinet_total_drawer_height)
                : formValues.height
        );
    }, [formValues]);

    const generateDrawers = useCallback(
        (
            drawers: Drawer[],
            isInnerDrawer = false,
            containerHeight = formValues.height,
            containerWidth = formValues.width
        ) => {
            return getDrawers(
                drawers,
                formValues,
                variables,
                {
                    ...values,
                    cabinet_carc_thickness: carcaseThickness,
                    cabinet_ext_thickness: exteriorThickness,
                },
                isInnerDrawer,
                containerHeight,
                containerWidth,
                formValues.hasDoor
            );
        },
        [formValues]
    );

    const generateInnerDrawer = () => {
        return getInnerDrawers(
            {
                ...values,
                cabinet_carc_thickness: carcaseThickness,
                cabinet_ext_thickness: exteriorThickness,
            },
            variables,
            formValues,
            innerDrawerFaceHeight
        );
    };

    const generateInnerDrawerOnly = () => {
        const drawers = productDataStore.current?.drawers;
        let height = 0;
        let depth = 0;
        const innerDrawers: Drawer[] = [];
        const drawer = values?.drawers?.[0];
        if (drawer?.drawer_runner_specs) {
            height = drawer?.drawer_runner_height;
            innerDrawers.push({
                drawer_runner_height: height,
                drawer_face_height: height,
                drawer_runner_specs: drawer?.drawer_runner_specs,
                drawer_type: 1,
            });
        } else if (drawers?.length) {
            // handling for the standalone 3D preview
            height = drawers[0].drawer_runner_height;
            depth = drawers[0].drawer_runner_depth;
            innerDrawers.push({
                ...drawers[0],
                drawer_face_height: drawers[0].drawer_runner_height,
                drawer_runner_specs: `${height}x${depth} : White D`,
            });
        }
        return getDrawers(
            innerDrawers,
            formValues,
            variables,
            {
                ...values,
                cabinet_carc_thickness: carcaseThickness,
                cabinet_ext_thickness: exteriorThickness,
            },
            true,
            height,
            values.cabinet_panel_width
        );
    };

    const generateDrawerFace = () => {
        if (!formValues.drawerFaceHeights) return;

        const drawerFaces = [
            ...(formValues.drawerFaceHeights ?? []),
        ]?.reverse(); // reverse to match drawer face position
        let currentPosition = 0;

        const result = {
            drawers: drawerFaces?.map((faceHeight, index) => {
                const prevFaceHeight = drawerFaces[index - 1];
                if (prevFaceHeight) {
                    currentPosition +=
                        Number(prevFaceHeight) + Number(formValues.drawerGap);
                }
                return {
                    index,
                    faceHeight,
                    position: currentPosition,
                };
            }),
        };

        return result;
    };

    const drawerValues: ConditionalValues[] = [
        {
            key: 'drawerDefault',
            condition: Boolean(formValues.drawers),
            callback: generateDrawerPosition,
        },
        {
            key: 'innerDrawers',
            condition: Boolean(
                variables.innerDrawerCount ??
                (values.drawer_amount && values.drawers?.length)
            ),
            callback: generateInnerDrawer,
        },
        {
            key: 'drawerFace',
            condition: Boolean(formValues.drawerFaceHeights),
            callback: generateDrawerFace,
        },
        {
            key: 'innerDrawers',
            condition: Boolean(variables.innerDrawerOnly),
            callback: generateInnerDrawerOnly,
        },
    ];

    return {
        drawerValues,
    };
};

export default useDrawerDynamicValues;
